import React, { useState } from "react";
import { Stack, Heading, Center } from "@chakra-ui/react";
import PropTypes from "prop-types";
import { Markup } from "interweave";
import { uniq, flatten } from "lodash";
import { Link } from "gatsby";

import Authors from "./Authors";
import TextBlock from "./TextBlock";
import Figure from "./Figure";
import References from "./References";
import Contributions from "./Contributions";
import History from "./History";
import ArticleMenu from "./ArticleMenu";
import Corrections from "./Corrections";
import ExtendedData from "./ExtendedData";
import Proteopedia from "./Proteopedia";

const initials = (name) => {
  const matches = name.match(/\b(\w)/g);
  return matches && matches.join("");
};

const authorsString = (authors) => {
  if (!authors || authors.length === 0) return "";

  if (authors.length > 8) {
    const lastAuthor = authors[authors.length - 1];
    return `${authors
      .slice(0, 6)
      .map((author) => `${author.lastName}, ${initials(author.firstName)}`)
      .join("; ")}; et al.; ${lastAuthor.lastName}, ${initials(
      lastAuthor.firstName
    )}`;
  }
  return authors
    .map((author) => `${author.lastName}, ${initials(author.firstName)}`)
    .join("; ");
};

const Article = (props) => {
  const { article } = props;

  const {
    doi,
    history,
    version,
    submissionTypes,
    id,
    pmcId,
    pmId,
    species,
    corrections,
    reviewers,
    proteopedia,
    reviewPanel,
  } = article;

  const {
    title,
    authors,
    abstract,
    image,
    imageTitle,
    imageCaption,
    patternDescription,
    methods,
    reagents,
    references,
    acknowledgements,
    funding,
    extendedData,
  } = version;

  const [showProteopedia, setShowProteopedia] = useState(false);

  const year =
    history && history.published
      ? new Date(history.published).getFullYear()
      : "2022";
  const copyright = `\u00a9 ${year} by the authors. This is an open-access article distributed under the terms of the Creative Commons Attribution 4.0 International (CC BY 4.0) 
  License, which permits unrestricted use, distribution, and reproduction in any medium, provided the original author and source are credited.`;

  const reviewedBy = uniq(flatten(reviewers))
    .filter((r) => r)
    .join(", ");

  const titleWithoutP = title.replace("<p>", "").replace("</p>", "");

  const citationString = `${authorsString(
    authors
  )} (${year}). ${titleWithoutP}. microPublication Biology. <a href="https://doi.org/${doi}" target="_blank" rel="noopener noreferrer">${doi}</a>.`;

  const extension = image.url.split(".").pop();
  const articleId =
    doi && doi.includes("/") && doi.split("/")[1].replace(/\./g, "-");
  const figure = `/static/figures/${articleId}.${extension}`;

  const extendedDataDoi = extendedData && extendedData.filter((e) => e.doi);

  return (
    <Center>
      <Stack paddingTop="100px" w={["75%", "75%", "75%", "50%"]}>
        <Heading fontFamily="Noto sans, sans-serif">
          <Markup content={title} />
        </Heading>
        <Authors authors={authors} />
        <Corrections corrections={corrections} />
        <ArticleMenu
          citation={citationString}
          submissionTypes={submissionTypes}
          title={title}
          articleId={id}
          versionId={version.id}
          doi={doi}
          pmcId={pmcId}
          pmId={pmId}
          species={species}
          showProteopediaButton={proteopedia}
          onClickShowProteopedia={() => setShowProteopedia(true)}
        />
        {proteopedia && (
          <Proteopedia
            proteopediaLink={proteopedia}
            showProteopedia={showProteopedia}
            setShowProteopedia={setShowProteopedia}
          />
        )}
        <TextBlock label="Abstract" text={abstract} isHtml />
        <Figure figure={figure} title={imageTitle} caption={imageCaption} />
        <TextBlock label="Description" text={patternDescription} isHtml />
        <TextBlock label="Methods" text={methods} isHtml doi={doi} />
        <TextBlock label="Reagents" text={reagents} isHtml />
        {extendedDataDoi && extendedDataDoi.length > 0 && (
          <ExtendedData extendedData={extendedDataDoi} />
        )}
        <TextBlock label="Acknowledgements" text={acknowledgements} isHtml />
        <TextBlock label="Funding" text={funding} />
        <Contributions authors={authors} />
        <TextBlock label="Reviewed By" text={reviewedBy} />
        {reviewPanel && (
          <>
            <Heading
              size="lg"
              fontWeight="normal"
              fontFamily="Noto sans, sans-serif"
            >
              Reviewed By
            </Heading>
            <u>
              <Link to="/review-panels">{reviewPanel}</Link>
            </u>
          </>
        )}
        <History history={history} />
        <References references={references} />
        <TextBlock label="Copyright" text={copyright} />
        <TextBlock label="Citation" text={citationString} isHtml />
        {pmcId && <TextBlock text={`PubMed Central: ${pmcId}`} />}
        {pmId && <TextBlock text={`PubMed: ${pmId}`} />}
      </Stack>
    </Center>
  );
};

Article.propTypes = {
  article: PropTypes.shape({
    versions: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        authors: PropTypes.arrayOf(
          PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            credit: PropTypes.arrayOf(PropTypes.string),
            email: PropTypes.string,
            correspondingAuthor: PropTypes.bool,
            equalContribution: PropTypes.bool,
            affiliations: PropTypes.arrayOf(PropTypes.string),
          })
        ),
        abstract: PropTypes.string,
        image: PropTypes.shape({ url: PropTypes.string }),
        imageTitle: PropTypes.string,
        imageCaption: PropTypes.string,
        description: PropTypes.string,
        methods: PropTypes.string,
        reagents: PropTypes.string,
        references: PropTypes.arrayOf(PropTypes.shape({})),
        acknowledgements: PropTypes.string,
        funding: PropTypes.string,
        reviewers: PropTypes.arrayOf(PropTypes.shape({})),
        extendedData: PropTypes.arrayOf(PropTypes.shape({})),
      })
    ),
    history: PropTypes.shape({}),
    doi: PropTypes.string,
    submissionTypes: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.string,
    pmId: PropTypes.string,
    pmcId: PropTypes.string,
    species: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default Article;
